// client/src/coms/accounts/useAccountSessionGuard.js
// Keeps a page bound to one account: when the account behind this page changes (switched in another tab, signed
// in as someone else in a popup, signed out, or this device revoked from elsewhere) the app restarts instead of
// showing one account's data to another.
import { useEffect, useRef } from 'react';
import { fetchCurrentUserId, restartApp, subscribeAccountChanges } from './accountsApi';

export default function useAccountSessionGuard(user) {
  const uid = user && user.id != null ? String(user.id) : null;
  const uidRef = useRef(uid);
  uidRef.current = uid;

  useEffect(() => {
    if (!uid) return undefined;
    let checking = false;
    const verify = async () => {
      if (checking) return;
      checking = true;
      try {
        const now = await fetchCurrentUserId();
        if (now !== undefined && now !== uidRef.current) restartApp();
      } finally {
        checking = false;
      }
    };
    const offBroadcast = subscribeAccountChanges((nextId) => {
      if (String(nextId == null ? '' : nextId) !== String(uidRef.current || '')) restartApp();
    });
    const onSessionEnded = () => verify();
    window.addEventListener('vox-session-ended', onSessionEnded);
    // Returning to the tab after a while: make sure it is still the same account.
    const onVisible = () => {
      if (document.visibilityState === 'visible') verify();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      offBroadcast();
      window.removeEventListener('vox-session-ended', onSessionEnded);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [uid]);
}
